"use client";

import Image from "next/image";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useMediaQuery } from "usehooks-ts";
import { usePathname } from "next/navigation";
import { Button } from "@workspace/ui/components";
import { cn } from "@workspace/ui/lib/utils";
import Logo from "@/public/logo.svg";
import Lottie, { LottieRefCurrentProps, useLottie } from "lottie-react";
import logoAnimation from "@/public/animations/logo.json";

export const LandingHeader = ({
  variant = "brand",
}: {
  variant?: "brand" | "light";
}) => {
  const logoRef = useRef<LottieRefCurrentProps>(null);
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [hasScroll, setHasScroll] = useState(false);
  const isWideScreen = useMediaQuery("(min-width: 1024px)", {
    defaultValue: true,
    initializeWithValue: false,
  });
  const isActive = (target: string) => pathname === target;

  useEffect(() => {
    if (isWideScreen) {
      setIsOpen(false);
    }
  }, [isWideScreen]);

  useEffect(() => {
    const checkScroll = () => {
      const scrollTop = document.documentElement.scrollTop;
      setHasScroll(scrollTop >= 10);
    };

    checkScroll();
    window.addEventListener("scroll", checkScroll);

    return () => {
      window.removeEventListener("scroll", checkScroll);
    };
  }, []);

  return (
    <>
      {isOpen && (
        <motion.span
          initial={{ opacity: 0, backdropFilter: "blur(10px)" }}
          animate={{ opacity: 1, backdropFilter: "blur(1px)" }}
          className="fixed top-0 left-0 h-[100vh] w-full bg-[rgba(0,0,0,.4)] lg:hidden z-30"
        />
      )}

      <div
        className={cn(
          "sticky top-0 [--header-height:100px] z-30 p-4 transition-colors bg-[#fff3dc] justify-center flex items-center",
          hasScroll && "lg:bg-transparent",
        )}
      >
        <div
          className={cn(
            "h-fit flex items-center justify-between w-full max-w-[1200px] px-6 mx-auto border border-transparent transition-all duration-300",
            hasScroll &&
            "lg:rounded-[32px] lg:border-[#e3d8c6] lg:bg-[#f3e4c9]",
          )}
        >
          <Link href="/" className="w-[136px] min-w-[136px] h-[48px]">
            <Lottie
              lottieRef={logoRef}
              animationData={logoAnimation}
              autoPlay={false}
              loop={false}
              onClick={() => {
                logoRef.current?.setDirection(1);
                logoRef.current?.goToAndPlay(0);
              }}
            />
          </Link>

          <div className="flex items-center justify-end gap-5 w-full">
            <AnimatePresence>
              {(isOpen || isWideScreen) && (
                <motion.div
                  className={cn(
                    "absolute flex flex-col items-center bottom-1 left-0 w-full h-[calc(100vh-96px)] translate-y-full  bg-[#fff3dc] lg:relative lg:translate-y-0 lg:bottom-[unset] lg:h-fit transition-all duration-300",
                    hasScroll && "lg:bg-[#f3e4c9]",
                  )}
                >
                  <motion.div
                    initial={{ opacity: 0, backdropFilter: "blur(100px)" }}
                    animate={{ opacity: 1, backdropFilter: "blur(0px)" }}
                    className="flex flex-col w-full p-5 lg:flex-row gap-8 justify-end lg:items-center"
                  >
                    <Button variant="d-ghost" asChild>
                      <Link
                        onClick={() => setIsOpen(false)}
                        href="/pricing"
                        className={cn(
                          "text-lg text-d-gray font-semibold flex w-fit h-12 lg:h-fit py-2 px-4 items-center justify-center rounded-[12px]",
                          isActive("/pricing") && "lg:bg-[#f3e2c3]",
                        )}
                      >
                        Pricing
                      </Link>
                    </Button>
                    <Button variant="d-ghost" asChild>
                      <Link
                        href="https://form.typeform.com/to/PyPqtNed"
                        className="text-lg text-d-gray font-semibold flex w-fit h-12 lg:h-fit py-2 px-4 items-center justify-center"
                      >
                        Contact us
                      </Link>
                    </Button>
                    <Button
                      variant="d-primary"
                      className="w-fit h-12 lg:hidden"
                    >
                      See Team Gaps
                    </Button>
                  </motion.div>
                  <span className="text-[158px] font-extrabold leading-[158px] bg-[image:linear-gradient(180deg,#F3E4C9_0.26%,rgba(243,228,201,0.00)_90.67%)] bg-clip-text text-transparent lg:hidden absolute bottom-[-5%] left-1/2 -translate-x-1/2">
                    Yolk
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            <Button asChild variant="d-default" className="h-12">
              <Link href="/auth">Log In</Link>
            </Button>
            <Button
              onClick={() => setIsOpen(!isOpen)}
              variant="ghost"
              className="has-[svg]:px-0 lg:hidden"
            >
              {isOpen ? (
                <X className="size-[30px]" />
              ) : (
                <Menu className="size-[30px]" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
